/* ============================================================
   ZEN·REST motion & behaviour layer (v3).
   - IntersectionObserver scroll reveals (.zen-reveal -> .zen-in)
   - Layer explorer (sections/zen-layers.liquid)
   - Sticky CTA (sections/zen-sticky-cta.liquid)
   - Marquee pause when off-screen
   - Sakura petals (decorative, desktop pointer devices only)
   - Product intro variant/price sync (sections/zen-product-intro.liquid)
   No jQuery. No external dependencies. Respects reduced motion.
   Safe to load multiple times (Theme Editor section reloads).
   ============================================================ */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function init(root) {
    root = root || document;

    /* ---------- Scroll reveal ---------- */
    document.documentElement.classList.add('zen-motion-ready');
    var revealEls = root.querySelectorAll('.zen-reveal:not(.zen-in)');
    if (revealEls.length) {
      if (reduceMotion || !('IntersectionObserver' in window)) {
        revealEls.forEach(function (el) { el.classList.add('zen-in'); });
      } else {
        var io = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting) {
                entry.target.classList.add('zen-in');
                io.unobserve(entry.target);
              }
            });
          },
          { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
        );
        revealEls.forEach(function (el) { io.observe(el); });
      }
    }

    /* ---------- Marquee: pause when out of viewport ---------- */
    var marquees = root.querySelectorAll('.zen-marquee');
    if (marquees.length && 'IntersectionObserver' in window) {
      var mio = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          entry.target.classList.toggle('zen-paused', !entry.isIntersecting);
        });
      });
      marquees.forEach(function (m) { mio.observe(m); });
    }

    /* ---------- Layer explorer ---------- */
    root.querySelectorAll('[data-zen-layers]').forEach(function (section) {
      if (section.dataset.zenLayersInit) return;
      section.dataset.zenLayersInit = 'true';
      var items = section.querySelectorAll('[data-zen-layer-item]');
      var bars = section.querySelectorAll('[data-zen-layer-bar]');

      function activate(index, focus) {
        items.forEach(function (item, i) {
          var active = i === index;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-expanded', active ? 'true' : 'false');
          if (active && focus) item.focus();
        });
        bars.forEach(function (bar, i) {
          bar.classList.toggle('is-active', i === index);
        });
      }

      items.forEach(function (item, i) {
        item.addEventListener('click', function () { activate(i, false); });
        item.addEventListener('keydown', function (e) {
          var next = null;
          if (e.key === 'ArrowDown') next = (i + 1) % items.length;
          if (e.key === 'ArrowUp') next = (i - 1 + items.length) % items.length;
          if (next !== null) {
            e.preventDefault();
            activate(next, true);
          }
        });
      });
      if (items.length) activate(0, false);
    });

    /* ---------- Sticky CTA ---------- */
    root.querySelectorAll('[data-zen-sticky]').forEach(function (bar) {
      if (bar.dataset.zenStickyInit) return;
      bar.dataset.zenStickyInit = 'true';
      var dismissed = false;
      var closeBtn = bar.querySelector('[data-zen-sticky-close]');
      if (closeBtn) {
        closeBtn.addEventListener('click', function () {
          dismissed = true;
          bar.classList.remove('is-visible');
        });
      }
      var threshold = parseInt(bar.dataset.zenSticky, 10) || 600;
      var ticking = false;
      function onScroll() {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(function () {
          if (!dismissed) {
            var nearBottom =
              window.innerHeight + window.scrollY >
              document.documentElement.scrollHeight - 420;
            bar.classList.toggle(
              'is-visible',
              window.scrollY > threshold && !nearBottom
            );
          }
          ticking = false;
        });
      }
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    });

    /* ---------- Sakura petals (decorative) ---------- */
    if (!reduceMotion && window.matchMedia('(pointer: fine)').matches) {
      root.querySelectorAll('[data-zen-sakura]').forEach(function (host) {
        if (host.dataset.zenSakuraInit) return;
        host.dataset.zenSakuraInit = 'true';
        var count = Math.min(parseInt(host.dataset.zenSakura, 10) || 10, 14);
        var depths = ['near', 'mid', 'far'];
        for (var i = 0; i < count; i++) {
          var petal = document.createElement('span');
          petal.className =
            'zen-sakura__petal zen-sakura__petal--' + depths[i % 3];
          var size = 8 + (i % 4) * 3;
          petal.style.left = ((i * 83) % 100) + '%';
          petal.style.setProperty('--d', 13 + ((i * 7) % 9) + 's');
          petal.style.setProperty('--delay', '-' + ((i * 3.7) % 14) + 's');
          var inner = document.createElement('span');
          inner.className = 'zen-sakura__inner';
          inner.style.display = 'block';
          inner.style.width = size + 'px';
          inner.style.height = size + 'px';
          petal.appendChild(inner);
          host.appendChild(petal);
        }
      });
    }

    /* ---------- Product intro: variant select -> price + form sync ---------- */
    root.querySelectorAll('[data-zen-product-intro]').forEach(function (wrap) {
      if (wrap.dataset.zenIntroInit) return;
      wrap.dataset.zenIntroInit = 'true';
      var select = wrap.querySelector('[data-zen-variant-select]');
      var priceEl = wrap.querySelector('[data-zen-price]');
      var compareEl = wrap.querySelector('[data-zen-compare-price]');
      var idInput = wrap.querySelector('input[name="id"]');
      var button = wrap.querySelector('[data-zen-atc]');
      var buttonText = wrap.querySelector('[data-zen-atc-text]');
      if (!select) return;
      function sync() {
        var opt = select.options[select.selectedIndex];
        if (!opt) return;
        if (idInput) idInput.value = opt.value;
        if (priceEl && opt.dataset.price) priceEl.textContent = opt.dataset.price;
        if (compareEl) {
          if (opt.dataset.comparePrice) {
            compareEl.textContent = opt.dataset.comparePrice;
            compareEl.hidden = false;
          } else {
            compareEl.hidden = true;
          }
        }
        var available = opt.dataset.available === 'true';
        if (button) {
          button.disabled = !available;
          if (buttonText) {
            buttonText.textContent = available
              ? button.dataset.labelAdd
              : button.dataset.labelSoldOut;
          }
        }
      }
      select.addEventListener('change', sync);
      sync();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
})();
